import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Form,
    Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import "react-toastify/dist/ReactToastify.css";
import { Trash2 } from "react-feather";

const PAGE_SIZES = [10, 25, 50, 100];

// DB is_active: 1 = Active, 0 = Inactive
const USER_STATUS_OPTIONS = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
];

const ManageUsers = () => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const isSuperDenied = userData.is_super === 0 || userData.is_super === "0";

    const [users, setUsers] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [deletingUserId, setDeletingUserId] = useState(null);

    const animatedComponents = makeAnimated();
    const selectColors = {
        primary: "#007bff",
        primary25: "#cce5ff",
        approved: "green",
        rejected: "#6c757d",
        lightGray: "#f8f9fa",
        darkGray: "#343a40",
    };
    const customStylesSelect = {
        control: (provided, state) => ({
            ...provided,
            minWidth: "120px",
            borderColor: state.isFocused ? selectColors.primary : provided.borderColor,
            boxShadow: state.isFocused ? `0 0 0 1px ${selectColors.primary}` : provided.boxShadow,
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: state.data?.value === 1 ? selectColors.approved : selectColors.rejected,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? (state.data.value === 1 ? selectColors.approved : selectColors.rejected) : state.isFocused ? selectColors.primary25 : selectColors.lightGray,
            color: state.isSelected || state.isFocused ? "#fff" : selectColors.darkGray,
        }),
    };

    const fetchUsers = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        setError(null);
        try {
            const res = await AxiosInstance().post("/admin/user/get_users", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "created_at",
                sort_dir: sortDir || "desc",
            });
            setUsers(Array.isArray(res.data?.data) ? res.data.data : []);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load users");
            setUsers([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isSuperDenied) {
            setLoading(false);
            setError("Permission denied. Super Admin only.");
            return;
        }
        fetchUsers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [isSuperDenied, currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchUsers]);

    const loadData = useCallback(() => {
        fetchUsers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [fetchUsers, currentPage, perPage, searchApplied, sortColumn, sortDirection]);

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "created_at";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const fullName = (u) => [u.firstname, u.lastname].filter(Boolean).join(" ") || "—";

    const handleStatusChange = async (userId, newValue) => {
        const isActive = newValue === 1;
        setUpdatingStatusId(userId);
        try {
            const res = await AxiosInstance().post("/admin/user/update_status", {
                user_id: userId,
                is_active: isActive ? 1 : 0,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Status updated");
                fetchUsers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const currentUserId = userData.user_id ?? userData.admin_id ?? userData.id ?? null;
    const handleDeleteUser = async (user) => {
        if (Number(user.user_id) === Number(currentUserId)) {
            toast.error("You cannot delete your own account");
            return;
        }
        if (!window.confirm(`Delete user "${(user.firstname || "").trim()} ${(user.lastname || "").trim()}". This cannot be undone.`)) return;
        setDeletingUserId(user.user_id);
        try {
            const res = await AxiosInstance().post(`/admin/user/delete/${user.user_id}`, {});
            if (res.data.status) {
                toast.success(res.data.message || "User deleted");
                fetchUsers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Delete failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Delete failed");
        } finally {
            setDeletingUserId(null);
        }
    };

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            id: "firstname",
            name: "Name",
            selector: (row) => fullName(row),
            sortable: true,
            grow: 1.5,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-medium">{fullName(row)}</span>
                    {(row.is_super === "1" || row.is_super === 1) && (
                        <span className="badge bg-info">Super</span>
                    )}
                </div>
            ),
        },
        {
            id: "email",
            name: "Email",
            selector: (row) => row.email || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            id: "mobile_no",
            name: "Mobile",
            selector: (row) => row.mobile_no || "—",
            sortable: true,
            width: "120px",
        },
        {
            id: "user_role_title",
            name: "Role",
            selector: (row) => row.user_role_title || "—",
            sortable: true,
            width: "120px",
        },
        {
            name: "Status",
            width: "200px",
            center: true,
            cell: (row) => {
                const dbValue = row.is_active === "1" || row.is_active === 1 ? 1 : 0;
                const valueOption = USER_STATUS_OPTIONS.find((opt) => opt.value === dbValue) || USER_STATUS_OPTIONS[0];
                return (
                    <div className="d-flex align-items-center justify-content-center">
                        <Select
                            components={animatedComponents}
                            className="basic-single"
                            classNamePrefix="select"
                            value={valueOption}
                            onChange={(selectedOption) => selectedOption && handleStatusChange(row.user_id, selectedOption.value)}
                            options={USER_STATUS_OPTIONS}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                                ...customStylesSelect,
                                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                            }}
                            isDisabled={updatingStatusId === row.user_id}
                            isSearchable={false}
                            menuPlacement="auto"
                        />
                    </div>
                );
            },
        },
        {
            id: "created_at",
            name: "Created",
            selector: (row) => row.created_at || "—",
            sortable: true,
            width: "110px",
            cell: (row) => <span className="text-muted small">{formatDate(row.created_at)}</span>,
        },
        {
            name: "Actions",
            sortable: false,
            width: "80px",
            center: true,
            cell: (row) => {
                const isCurrentUser = currentUserId != null && Number(row.user_id) === Number(currentUserId);
                return (
                    <div className="d-flex justify-content-center align-items-center gap-1">
                        {!isCurrentUser && (
                            <button
                                type="button"
                                className="btn btn-link p-0 text-danger border-0 bg-transparent"
                                onClick={() => handleDeleteUser(row)}
                                disabled={deletingUserId === row.user_id}
                                title="Delete User"
                            >
                                <Trash2 size={18} className="delete-icon" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    if (isSuperDenied && !loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Users" mainTitle="Admin" />
                <Row>
                    <Col xs="12">
                        <Card className="border-0 shadow-sm">
                            <CardBody className="text-center py-5">
                                <i className="fa fa-lock fa-3x text-warning mb-3" />
                                <h5 className="text-danger mb-2">Access Denied</h5>
                                <p className="text-muted mb-0">Only Super Admin can manage users.</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    if (loading && users.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Users" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && users.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Manage Users" mainTitle="Admin" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={loadData}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Manage Users" mainTitle="Admin" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form className="theme-form me-2 flex-grow-1" onSubmit={handleSearchSubmit}>
                                    <div className="input-group flex-nowrap" style={{ height: 35, maxWidth: "350px" }}>
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by name, email, mobile or role..."
                                        />
                                        <Button
                                            color="primary"
                                            type="submit"
                                            className="btn-primary input-group-text pe-3 ps-3"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center ms-auto mt-3 mt-md-0 gap-2">
                                    <Button color="primary" onClick={() => navigate("/job/admin/add-user")} className="d-flex align-items-center">
                                        <i className="fa fa-plus me-1" /> Add User
                                    </Button>
                                    <Button color="primary" outline onClick={loadData} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={users}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortFieldId="created_at"
                                    defaultSortAsc={false}
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No users found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <ToastContainer />
        </Fragment>
    );
};

export default ManageUsers;
